import React from 'react';

export interface PartenaireItem {
  id: number;
  code: string;
  nom: string;
  description: string | null;
  discord_link: string;
  color: string;
  icon: string | null;
  ordre: number;
  actif: number;
  created_at?: string;
}

const PARTENAIRE_COLORS = ['blue', 'red', 'green', 'purple', 'yellow', 'orange'] as const;

interface PartenairesTabProps {
  t: (key: string) => string;
  loading: boolean;
  partenairesConfig: PartenaireItem[];
  partnerForm: {
    code: string;
    nom: string;
    description: string;
    discord_link: string;
    color: typeof PARTENAIRE_COLORS[number];
    icon: string;
    ordre: number;
    actif: number;
  };
  setPartnerForm: React.Dispatch<React.SetStateAction<{
    code: string;
    nom: string;
    description: string;
    discord_link: string;
    color: typeof PARTENAIRE_COLORS[number];
    icon: string;
    ordre: number;
    actif: number;
  }>>;
  editingPartnerId: number | null;
  savingPartner: boolean;
  handleSavePartner: () => void;
  handleCancelEditPartner: () => void;
  handleEditPartner: (p: PartenaireItem) => void;
  handleDeletePartner: (id: number) => void;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const PartenairesTab: React.FC<PartenairesTabProps> = ({
  t,
  loading,
  partenairesConfig,
  partnerForm,
  setPartnerForm,
  editingPartnerId,
  savingPartner,
  handleSavePartner,
  handleCancelEditPartner,
  handleEditPartner,
  handleDeletePartner,
}) => (
  <div className="space-y-6">
    <p className="text-sm text-slate-400">{t('partenaires.description')}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={labelCls}>{t('partenaires.codeLabel')}</label>
        <input
          type="text"
          value={partnerForm.code}
          onChange={(e) => setPartnerForm((f) => ({ ...f, code: e.target.value }))}
          placeholder={t('partenaires.codePlaceholder')}
          className={inputCls}
          disabled={!!editingPartnerId}
        />
      </div>
      <div>
        <label className={labelCls}>{t('partenaires.nomLabel')}</label>
        <input
          type="text"
          value={partnerForm.nom}
          onChange={(e) => setPartnerForm((f) => ({ ...f, nom: e.target.value }))}
          placeholder={t('partenaires.nomPlaceholder')}
          className={inputCls}
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>{t('partenaires.descriptionLabel')}</label>
        <input
          type="text"
          value={partnerForm.description}
          onChange={(e) => setPartnerForm((f) => ({ ...f, description: e.target.value }))}
          placeholder={t('partenaires.descriptionPlaceholder')}
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>{t('partenaires.discordLabel')}</label>
        <input
          type="url"
          value={partnerForm.discord_link}
          onChange={(e) => setPartnerForm((f) => ({ ...f, discord_link: e.target.value }))}
          placeholder={t('partenaires.discordPlaceholder')}
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>{t('partenaires.couleurLabel')}</label>
        <select
          value={partnerForm.color}
          onChange={(e) => setPartnerForm((f) => ({ ...f, color: e.target.value as typeof PARTENAIRE_COLORS[number] }))}
          className={inputCls}
        >
          {PARTENAIRE_COLORS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <p className="text-xs text-slate-400">{t('partenaires.discordLogoNote')}</p>
      </div>
      <div>
        <label className={labelCls}>{t('partenaires.ordreLabel')}</label>
        <input
          type="number"
          min={0}
          value={partnerForm.ordre}
          onChange={(e) => setPartnerForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
          className={inputCls}
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="partner-actif"
          checked={!!partnerForm.actif}
          onChange={(e) => setPartnerForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
          className="rounded border-[#2a3a62] bg-[#0a132b]"
        />
        <label htmlFor="partner-actif" className="text-sm text-slate-300">{t('partenaires.actifLabel')}</label>
      </div>
    </div>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={handleSavePartner}
        disabled={savingPartner}
        className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-medium text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors disabled:opacity-50"
      >
        {savingPartner ? t('partenaires.saving') : editingPartnerId ? t('partenaires.update') : t('partenaires.add')}
      </button>
      {editingPartnerId && (
        <button
          type="button"
          onClick={handleCancelEditPartner}
          className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-black/45 transition-colors"
        >
          {t('partenaires.cancel')}
        </button>
      )}
    </div>
    <div className="border-t border-[#1e2d4d] pt-4">
      <h2 className="text-sm font-semibold text-slate-200 mb-3">{t('partenaires.listTitle')}</h2>
      {loading ? (
        <p className="text-sm text-slate-500">{t('partenaires.loading')}</p>
      ) : partenairesConfig.length === 0 ? (
        <p className="text-sm text-slate-500">{t('partenaires.empty')}</p>
      ) : (
        <ul className="divide-y divide-[#1e2d4d]">
          {partenairesConfig.map((p) => (
            <li key={p.id} className="py-2 flex items-center justify-between gap-4">
              <span className={`flex-1 ${p.actif ? 'text-slate-200' : 'text-slate-500'}`}>
                {p.nom} <span className="text-slate-500">({p.code})</span>
                {!p.actif && <span className="ml-2 text-xs text-amber-400">{t('partenaires.inactif')}</span>}
              </span>
              <span className="text-xs text-slate-500">{t('partenaires.ordre')}: {p.ordre}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEditPartner(p)}
                  className="rounded border border-white/15 bg-black/30 px-2 py-1 text-xs font-medium text-slate-200 hover:bg-black/45 transition-colors"
                >
                  {t('partenaires.modify')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeletePartner(p.id)}
                  className="rounded border border-red-500/35 bg-red-500/10 px-2 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors"
                >
                  {t('partenaires.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default PartenairesTab;
